import React from 'react';
import {
  HandCoins,
  Clock,
  Percent,
  CalendarClock,
  ClipboardList,
} from 'lucide-react';
import { ScopeBlocks } from '@/components/estimates/ScopeProposal';

/* ============================================================================
 * TMAgreementBody — renders a Time & Material agreement (layout === 'tm').
 * Mirrors K.O.'s real T&M agreement: deposit/retainer, hourly rate, material
 * markup, submission + payment terms, and start/finish dates. The branded
 * header, client block and the acceptance/signature page are provided by the
 * host (EstimatePreview); this is just the agreement body.
 * ========================================================================== */

const money = (v) => {
  const s = String(v || '').trim();
  if (!s) return '';
  return /^[\d.,]+$/.test(s) ? `$${s}` : s;
};

const Term = ({ icon: Icon, label, children, testId }) =>
  children ? (
    <div className="px-4 py-3 border-t border-border first:border-t-0" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
        {Icon ? <Icon size={11} className="text-[hsl(var(--primary))]" /> : null}
        {label}
      </div>
      <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
        {children}
      </div>
    </div>
  ) : null;

export const TMAgreementBody = ({ estimate }) => {
  const tm = estimate?.tm_terms || {};
  const rate = String(tm.hourly_rate || '').trim();
  const markup = String(tm.material_markup_pct || '').trim();
  // Scope of Work: T&M sections carry written blocks only (no priced tables).
  const scopeSections = (Array.isArray(estimate?.sections) ? estimate.sections : [])
    .filter((s) => Array.isArray(s?.blocks) && s.blocks.length > 0);

  return (
    <section className="mt-6" data-testid="tm-agreement-body">
      {scopeSections.length > 0 ? (
        <div className="mb-6 space-y-5" data-testid="tm-scope-of-work">
          {scopeSections.map((s) => (
            <div key={s.id}>
              {s.name ? (
                <h2 className="text-lg font-semibold text-[hsl(var(--primary))] mb-2.5">
                  {s.name}
                </h2>
              ) : null}
              <ScopeBlocks blocks={s.blocks} />
            </div>
          ))}
        </div>
      ) : null}
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList size={16} className="text-[hsl(var(--primary))]" />
        <h2 className="text-lg font-semibold text-[hsl(var(--primary))]">
          Time &amp; Material Agreement
        </h2>
      </div>

      <div className="rounded-md border border-border overflow-hidden">
        <Term icon={HandCoins} label="Deposit / Retainer Required" testId="tm-deposit">
          {money(tm.deposit)}
        </Term>
        <Term icon={Clock} label="Hourly Rate" testId="tm-rate">
          {rate ? `${money(rate)} per hour` : ''}
        </Term>
        <Term icon={Percent} label="Material Markup" testId="tm-markup">
          {markup
            ? `${markup}% added to all material purchased and/or delivered by the Contractor.`
            : ''}
        </Term>
        <Term icon={ClipboardList} label="Record of Hours & Receipts" testId="tm-submission">
          {tm.submission_terms}
        </Term>
        <Term icon={HandCoins} label="Payment Terms" testId="tm-payment">
          {tm.payment_terms}
        </Term>
        <Term icon={CalendarClock} label="Start / Finish" testId="tm-dates">
          {tm.start_date || tm.finish_date
            ? `Start: ${tm.start_date || 'TBD'}\nFinish: ${tm.finish_date || 'TBD'}`
            : ''}
        </Term>
        <Term icon={ClipboardList} label="Additional Notes" testId="tm-notes">
          {tm.notes}
        </Term>
      </div>
    </section>
  );
};

export default TMAgreementBody;
